import { useState, useReducer, useCallback } from 'react'
import constate from 'constate'
import isEmpty from 'lodash/isEmpty'
import { add } from 'date-fns'

export const [FormProvider, useFormContext] = constate(useStep)

export function useStep({ ...rest }) {
  const [step, setStep] = useState(0)
  const handleNext = () => setStep(step + 1)
  const handlePrev = () => setStep(step - 1)

  return { step, handleNext, handlePrev, ...rest }
}

export const validateForm = (state, step) => {
  let error = false
  Object.keys(state).forEach((key) => {
    const datum = state[key]
    const required = datum?.validation?.required
    const value = datum?.value
    const datumStep = datum?.step
    const pattern = datum?.validation?.pattern
    if (datumStep === step) {
      if (required) {
        if (validateInput(key, value, required)) {
          error = true
        }
      } else if (value && pattern) {
        if (validateInput(key, value, required, pattern)) {
          error = true
        }
      }
    }
  })
  return error
}

/**
 *
 * @param {*} name
 * @param {*} value
 * @param {*} required
 * @returns Boolean
 */
export function validateInput(name = '', value, required = false, pattern = '') {
  let hasError = false
  if (required || pattern) {
    const emptyStringValue = typeof value === 'string' && (value.trim() === '' || value.trim() === 'default')
    const emptyObjectValue = typeof value === 'object' && !isEmpty(value)
    const patternMatchFailed = pattern && !value.match(pattern)
    if (emptyStringValue || emptyObjectValue || patternMatchFailed) {
      hasError = true
    } else {
      hasError = false
    }
  }
  return hasError
}

export const initialState = {
  save_data: {
    value: false,
    can_save: true,
  },
  customer_attachments: [],
  request_type: {
    value: 'analyst_investigation',
    error: '',
    touched: false,
    valid: true,
    step: 0,
    validation: {
      required: true
    }
  },
  preferred_delivery_date: {
    value: add(new Date(), { days: 1 }),
    error: '',
    touched: false,
    valid: false,
    step: 0,
    validation: {
      required: true
    }
  },
  detail_amount: {
    value: '',
    error: '',
    touched: false,
    valid: false,
    step: 0,
    validation: {
      required: true
    }
  },
  description: {
    value: '',
    error: '',
    touched: false,
    valid: false,
    step: 0,
    validation: {
      required: true
    }
  },
  subject: {
    value: '',
    error: '',
    touched: false,
    valid: false,
    step: 0,
    validation: {
      required: true
    }
  },
  // sensitivity_level: {
  //   value: 'Green',
  //   error: '',
  //   touched: false,
  //   valid: false,
  //   step: 0,
  //   validation: {
  //     required: true
  //   }
  // },
  work_start_choice: {
    value: 'before',
    error: '',
    touched: false,
    valid: false,
    step: 0,
    validation: {
      required: true
    }
  },
  contact_preference: {
    value: 'email',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    validation: {
      required: false
    }
  },
  contactPhone: {
    value: '',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    validation: {
      required: false
    }
  },
  visibility_option: {
    value: 'me',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    validation: {
      required: false
    }
  },
  visibilityEmailList: {
    value: '',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    validation: {
      required: false,
      pattern: /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/
    }
  },
  preferred_delivery_method: {
    value: '',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    step: 1,
    validation: {
      required: true
    }
  },
  availability_timezone: {
    value: '',
    can_save: true,
    error: '',
    touched: false,
    valid: false,
    validation: {
      required: false
    }
  },
  availability_days: {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false
  },
  availability_hours: {
    '01:00': false,
    '02:00': false,
    '03:00': false,
    '04:00': false,
    '05:00': false,
    '06:00': false,
    '07:00': false,
    '08:00': false,
    '09:00': false,
    '10:00': false,
    '11:00': false,
    '12:00': false,
    '13:00': false,
    '14:00': false,
    '15:00': false,
    '16:00': false,
    '17:00': false,
    '18:00': false,
    '19:00': false,
    '20:00': false,
    '21:00': false,
    '22:00': false,
    '23:00': false,
    '24:00': false,
  },
}

export const LOCAL_STORAGE_KEY = 'eod:investigationForm'

export function initStateFromLocalStorage() {
  try {
    const localStorageValue = localStorage.getItem(LOCAL_STORAGE_KEY)

    if (localStorageValue !== null) {
      const savedItems = JSON.parse(localStorageValue)
      // merge with initial state
      return { ...initialState, ...savedItems }
    }
  } catch {
    // if user is in incognito mode, `localStorage` access
    // will throw an error. if the value is malformed JSON
    // `JSON.parse` will throw an error as well
  }

  return initialState
}
export function usePersistReducer() {
  const reducerLocalStorage = useCallback((state, action) => reducer(state, action), [])

  // use `initStateFromLocalStorage` function to initialize state from `localStorage`
  return useReducer(reducerLocalStorage, undefined, initStateFromLocalStorage)
}

export function reducer(state, action) {
  switch (action.type) {
    case 'saveToStorage':
      return { ...state, save_data: { ...state.save_data, value: action.payload.checked } }
    case 'updateField':
      return { ...state, [action.payload.name]: { ...state[action.payload.name], value: action.payload.value, error: validateInput(action.payload.name, action.payload.value, state[action.payload.name]?.validation?.required, state[action.payload.name]?.validation?.pattern), touched: true } }
    case 'setError':
      return { ...state, [action.payload.name]: { ...state[action.payload.name], error: action.payload.error } }
    case 'updateAttachments':
      return { ...state, customer_attachments: action.payload }
    case 'removeAttachment':
      return { ...state, customer_attachments: state.customer_attachments.filter(attachment => attachment.file_name !== action.payload.name) }
    case 'clearError':
      return { ...state, [action.payload.name]: { ...state[action.payload.name], error: false } }
    case 'updateDaysAvailable':
      return { ...state, availability_days: { ...state.availability_days, [action.payload]: !state.availability_days[action.payload] } }
    case 'updateTimePicker':
      return { ...state, availability_hours: { ...state.availability_hours, [action.payload]: !state.availability_hours[action.payload] } }
    default:
      break
  }
}

export function formDataTransform(state) {
  const formBody = Object.entries(state).reduce((acc, [key, value]) => {
    if (key === 'availability_days' || key === 'availability_hours') {
      // return values that are true, otherwise return empty string
      return {
        ...acc,
        [key]: Object.entries(value).reduce((acc, [key, value]) => {
          if (value) {
            return [...acc, key]
          }
          return acc
        }, []).toString()
      }
    }
    if (key === 'contact_preference') {
      const newValue = value.value === 'email' ? 'email' : `phone ${state.contactPhone.value}`
      return { ...acc, [key]: newValue }
    }
    if (key === 'visibility_option') {
      const newValue = value.value === 'me_and_team' ? `Myself and team: ${state.visibilityEmailList.value}` : 'Just myself'
      return { ...acc, [key]: newValue }
    }
    if (key === 'work_start_choice') {
      const newValue = value.value === 'before' ? 'Talk to me before consuming entitlements to start work' : 'Consume investigation entitlements and begin work immediately'
      return { ...acc, [key]: newValue }
    }
    if (key === 'customer_attachments') {
      return { ...acc, [key]: value }
    }
    return { ...acc, [key]: value?.value?.toString() }
  }, {})
  delete formBody.contactPhone
  delete formBody.visibilityEmailList
  delete formBody.save_data

  return formBody
}
